import type { Turn } from '@chessmate/protocol';
import type {
  BackgammonDice,
  BackgammonPosition,
  BackgammonResult,
  BackgammonTurn,
} from './backgammon.js';
import {
  applyTurn,
  backgammonResultOf,
  compareTurnsInMoverFrame,
  legalTurns,
  pipCount,
} from './backgammon.js';

/**
 * The annealed robot: expectimax over the mover's legal turns, with the dice
 * as the only chance node. Greedy `evaluateTurn` plays the move that looks
 * best right now; this looks one opponent exchange ahead and plays the move
 * whose *expectation* over the opponent's 21 dice compositions is best.
 *
 * The evaluation stays deliberately honest — the same signals the greedy
 * robot uses (pip race, borne-off checkers, checkers on the bar), from a
 * fixed perspective so every leaf compares. Backgammon's real evaluation is
 * a research project; a kid's robot needs to be beatable, just less
 * accidentally so.
 */

/** Every distinct two-dice roll with its probability weight (out of 36):
 *  6 doubles at 1/36, 15 non-doubles at 2/36. Order within a roll is
 *  irrelevant to `legalTurns`, which enumerates orderings itself. */
export const DICE_COMPOSITIONS: readonly {
  readonly dice: BackgammonDice;
  readonly weight: number;
}[] = (() => {
  const out: { dice: BackgammonDice; weight: number }[] = [];
  for (let a = 1; a <= 6; a++) {
    for (let b = a; b <= 6; b++) {
      out.push({ dice: a === b ? [a, a] : [a, b], weight: a === b ? 1 : 2 });
    }
  }
  return out;
})();

export const DICE_WEIGHT_TOTAL = 36;

/** Static value of a position from `for`'s perspective: ahead in the race,
 *  more borne off, fewer of yours on the bar, more of theirs. Same signals
 *  and magnitudes as `evaluateTurn`, just positional rather than per-turn. */
export function evaluatePosition(pos: BackgammonPosition, side: Turn): number {
  const enemy = side === 'w' ? 'b' : 'w';
  const raced = pipCount(pos, enemy) - pipCount(pos, side);
  const off = (pos.off[side] - pos.off[enemy]) * 4;
  const bar = (pos.bar[enemy] - pos.bar[side]) * 6;
  return raced + off + bar;
}

const WIN = 1000;
const GAMMON = 1500;

function terminalValue(result: BackgammonResult, side: Turn): number {
  const magnitude = result.kind === 'elimination' ? WIN : GAMMON;
  return result.winner === side ? magnitude : -magnitude;
}

type Static = (pos: BackgammonPosition) => number;

/** Per-decision transposition cache. Different step orders reach the same
 *  position constantly (8→5,6→4 vs 6→4,8→5), and `legalTurns` enumeration
 *  is the expensive part — one Map per expectiTurn call collapses it. */
type TtCache = Map<string, number>;

/**
 * Depth-2 bounds (additive 2026-10-05). The exhaustive second exchange is
 * ~28,000x the cost of depth 1 (712s vs 25ms measured on a mid-game
 * position) — no exact pruning brings that inside a robot turn, because the
 * leaf values admit only ±1500 bounds and star-cuts on chance nodes stay
 * weak against that spread. So depth 2 is a BOUNDED probe: exact through the
 * first opponent exchange (all 21 rolls, every reply considered), then
 * capped candidate sets at the deepest level. The caps bias SAFE for a
 * kids' robot: ordering the opponent's leaf replies by their own static
 * eval means we may miss their true best refutation (we over-rate our
 * position -> slightly overconfident), and limiting our own deepest
 * follow-ups may miss our best continuation (we under-rate it). Both
 * directions weaken Clever a hair rather than sharpening it.
 */

/** Opponent leaf replies examined per roll at the deepest level. */
const DEPTH2_LEAF_REPLIES = 4;
/** My candidate follow-ups per roll at the deepest level. */
const DEPTH2_INNER_TURNS = 2;
/** Root turns receiving the bounded probe: the top few by their exact
 *  depth-1 expectation (candidate pruning — the probe's job is to
 *  discriminate between the moves that already look good, not to rescue
 *  clearly worse ones). */
const DEPTH2_ROOT_CANDIDATES = 4;
/**
 * Hard stop for the probe, counted in `legalTurns` enumerations (the
 * dominant cost — one is ~5-25µs). Deterministic: the same position always
 * probes the same amount, on any machine. When the budget runs out the
 * remaining candidates keep their exact depth-1 scores, so the answer
 * degrades gracefully toward depth 1 instead of freezing the UI thread —
 * the robot computes synchronously in the browser, and the exhaustive tree
 * measured 712s here.
 */
const DEPTH2_PROBE_CALLS = 8_000;
/** Threaded budget handle; null on the exact (depth-1) paths. */
/** Threaded budget handle; null on the exact (depth-1) paths. */
type Probe = { left: number };

/** How much a reply advances the opponent: pips spent, plus 12 per white
 *  checker sent to the bar — the same hit bonus the greedy evaluator uses.
 *  Ordering key for the bounded leaf cap; cheap by construction (no
 *  position materialisation). */
function replyDamage(turn: BackgammonTurn): number {
  let pips = 0;
  for (const step of turn.steps) {
    pips += step.pips;
    if (step.to === 'bar') pips += 12;
  }
  return pips;
}

function positionKey(pos: BackgammonPosition, dice: BackgammonDice | null): string {
  return (
    pos.points.join(',') +
    '|' +
    pos.bar.w +
    ':' +
    pos.bar.b +
    '|' +
    pos.off.w +
    ':' +
    pos.off.b +
    '|' +
    pos.turn +
    '|' +
    (dice ? dice.join(':') : '')
  );
}

/**
 * The opponent's-reply expectation for the position after my move: the
 * weighted average, over the 21 dice compositions, of the value after the
 * opponent's best reply (they maximise their own position, i.e. minimise
 * mine). `k` = how many further plies of MY moves remain to search after
 * that reply: 0 leaf-evaluates directly; 1 rolls the dice for me and moves
 * once more before the next leaf.
 */
function opponentExpectation(
  pos: BackgammonPosition,
  me: Turn,
  k: number,
  sv: Static,
  cache: TtCache,
  bounded: boolean,
  probe: Probe | null,
): number {
  // Out of budget: fall back to the static value for this subtree — the
  // caller's decision then leans on shallower information, by design.
  if (probe && probe.left <= 0) return sv(pos);
  const opponent = me === 'w' ? 'b' : 'w';
  let total = 0;
  for (const { dice, weight } of DICE_COMPOSITIONS) {
    if (probe) probe.left -= 1;
    const replies = legalTurns(pos, dice);
    let best: number;
    if (replies.length === 0) {
      // Opponent passes; the turn hands back to me.
      best =
        k > 0
          ? myRollExpectation(applyTurn(pos, { steps: [] }), me, k, sv, cache, bounded, probe)
          : sv(pos);
    } else {
      // Bounded leaves (depth 2's deepest level) examine only the replies
      // that LOOK most damaging: pip advance (plus bar hits, which a step
      // to the bar reveals), cheapest-first — no applyTurn until a reply is
      // actually examined. The ordering heuristic can only miss the true
      // best refutation, i.e. can only make Clever overconfident.
      const considered =
        bounded && replies.length > DEPTH2_LEAF_REPLIES
          ? [...replies]
              .sort((a, b) => replyDamage(b) - replyDamage(a))
              .slice(0, DEPTH2_LEAF_REPLIES)
          : replies;
      best = Number.NEGATIVE_INFINITY;
      for (const reply of considered) {
        const after = applyTurn(pos, reply);
        const result = backgammonResultOf(after);
        if (result) {
          best = Math.max(best, terminalValue(result, me));
          continue;
        }
        best = Math.max(
          best,
          k > 0 ? myRollExpectation(after, me, k, sv, cache, bounded, probe) : sv(after),
        );
      }
    }
    total += weight * best;
  }
  return total / DICE_WEIGHT_TOTAL;
}

/** My-move expectation: average over the 21 rolls I could receive, of my
 *  best turn's value with `k - 1` further plies after it. */
function myRollExpectation(
  pos: BackgammonPosition,
  me: Turn,
  k: number,
  sv: Static,
  cache: TtCache,
  bounded: boolean,
  probe: Probe | null,
): number {
  if (probe && probe.left <= 0) return sv(pos);
  let total = 0;
  for (const { dice, weight } of DICE_COMPOSITIONS) {
    const key = positionKey(pos, dice);
    const cached = cache.get(key);
    if (cached !== undefined) {
      total += weight * cached;
      continue;
    }
    if (probe) probe.left -= 1;
    const turns = legalTurns(pos, dice);
    let best: number;
    if (turns.length === 0) {
      best = opponentExpectation(
        applyTurn(pos, { steps: [] }),
        me,
        k - 1,
        sv,
        cache,
        bounded,
        probe,
      );
    } else {
      best = Number.NEGATIVE_INFINITY;
      // Bounded deepest level: only my most promising follow-ups (by static
      // eval) carry the second exchange — the rest cannot be searched.
      const considered =
        bounded && k === 1 && turns.length > DEPTH2_INNER_TURNS
          ? [...turns]
              .map((turn) => ({ turn, mine: sv(applyTurn(pos, turn)) }))
              .sort((a, b) => b.mine - a.mine)
              .slice(0, DEPTH2_INNER_TURNS)
              .map((e) => e.turn)
          : turns;
      for (const turn of considered) {
        const after = applyTurn(pos, turn);
        const result = backgammonResultOf(after);
        if (result) {
          best = Math.max(best, terminalValue(result, me));
          continue;
        }
        best = Math.max(best, opponentExpectation(after, me, k - 1, sv, cache, bounded, probe));
      }
    }
    cache.set(key, best);
    total += weight * best;
  }
  return total / DICE_WEIGHT_TOTAL;
}

/** Pick the turn with the best opponent-reply expectation. `depth` 1 looks
 *  one exchange ahead (my move → each opponent roll → their best reply →
 *  leaf) EXACTLY; 2 probes one exchange deeper, bounded — the first
 *  exchange stays exact, the deepest level examines only DEPTH2_LEAF_REPLIES
 *  opponent replies and DEPTH2_INNER_TURNS own follow-ups per roll (see the
 *  bounds note above; exhaustive depth 2 measured 712s — unplayable). Same
 *  pass contract as the greedy pick: an empty legal set means the caller
 *  passes. */
export function expectiTurn(
  game: { position: BackgammonPosition; dice: BackgammonDice | null },
  depth: 1 | 2,
): BackgammonTurn {
  const me = game.position.turn;
  if (!game.dice) throw new Error('backgammon: expectiTurn needs a roll');
  const sv: Static = (p) => evaluatePosition(p, me);
  const turns = legalTurns(game.position, game.dice);
  if (turns.length === 0) return { steps: [] };
  const cache: TtCache = new Map();
  // Depth 2 narrows candidates first: an exact depth-1 pass scores every
  // turn cheaply, and only the top few earn the bounded deeper probe. The
  // probe's job is discriminating between moves that already look good.
  const finals: { turn: BackgammonTurn; after: BackgammonPosition; score: number }[] = [];
  for (const turn of turns) {
    const after = applyTurn(game.position, turn);
    if (backgammonResultOf(after)) return turn; // finishing now is always best
    finals.push({ turn, after, score: opponentExpectation(after, me, 0, sv, cache, false, null) });
  }
  finals.sort((a, b) => b.score - a.score || compareTurnsInMoverFrame(me, a.turn, b.turn));
  // turns is non-empty (checked above), so finals carries at least one entry.
  const front = finals[0];
  if (depth === 1 || !front) return front ? front.turn : { steps: [] };
  const candidates = finals.slice(0, Math.min(DEPTH2_ROOT_CANDIDATES, finals.length));
  const probe: Probe = { left: DEPTH2_PROBE_CALLS };
  let bestTurn = front.turn;
  let bestScore = Number.NEGATIVE_INFINITY;
  for (const { turn, after } of candidates) {
    if (probe.left <= 0) break; // spent — remaining candidates keep depth-1 scores
    const score = opponentExpectation(after, me, 1, sv, cache, true, probe);
    if (score > bestScore) {
      bestScore = score;
      bestTurn = turn;
    }
  }
  return bestTurn;
}
